import type { NavUser } from "./visibleNavSections";

/**
 * Who may open a page. Named levels rather than a numeric rank: they need not be strictly
 * hierarchical, because each one delegates to a `User` getter and the getters already encode
 * the role cascade (`isAdmin` is true for developers, and so on).
 *
 *  - `public`    — anyone, signed in or not.
 *  - `guestOnly` — only when signed out (Login); a signed-in user is sent on.
 *  - `user`      — any signed-in user.
 *  - `admin`     — `user.isAdmin`.
 *  - `developer` — `user.isDeveloper`.
 *
 * A new level is one entry in `RULES` plus the getter on `NavUser` (e.g. a future
 * `mapmaker: (user) => isSignedIn(user) && !!user?.isMapmaker`).
 */
export type AccessLevel = "public" | "guestOnly" | "user" | "admin" | "developer";

const isSignedIn = (user: NavUser | undefined) => !!user?.id;

const RULES: Record<AccessLevel, (user: NavUser | undefined) => boolean> = {
	public: () => true,
	guestOnly: (user) => !isSignedIn(user),
	user: isSignedIn,
	admin: (user) => isSignedIn(user) && !!user?.isAdmin,
	developer: (user) => isSignedIn(user) && !!user?.isDeveloper,
};

/** The one place an access level is turned into a yes/no for a given user. Pure. */
export function accessAllows(level: AccessLevel, user: NavUser | undefined): boolean {
	return RULES[level](user);
}

/**
 * What the route guard does with a visitor:
 *  - `allow`  — render the page.
 *  - `signIn` — signed out on a page that needs a session: send to Login, coming back after.
 *  - `leave`  — signed in on a guest-only page (Login): send on to where they were going.
 *  - `deny`   — signed in but lacking the role: show Not-Authorized (not a 404 — the page exists).
 */
export type AccessDecision = "allow" | "signIn" | "leave" | "deny";

/**
 * The route guard's rule. `hasSession` is whether a session token is stored, which can be true
 * while `user` is absent — `/auth/me` failed with something other than a 401, which leaves the
 * session in place. Such a visitor still opens `user` pages, as they always have, but no role
 * page: the roles are unknown.
 */
export function accessDecision(
	level: AccessLevel,
	user: NavUser | undefined,
	hasSession: boolean = isSignedIn(user),
): AccessDecision {
	if (level === "public") return "allow";
	if (level === "guestOnly") return isSignedIn(user) ? "leave" : "allow";
	if (!hasSession && !isSignedIn(user)) return "signIn";
	if (level === "user") return "allow";
	return accessAllows(level, user) ? "allow" : "deny";
}
